import { Dtos } from "@inrev/common";
import { useForm } from "react-hook-form";
import { carrierLogoIconMap } from "../../utils/carrier";
import { typedZodResolver } from "../../utils/form";
import { FormNumber } from "../form/fields/FormNumber";
import { Button } from "../ui/Button";

const formatBondNumber = (value: number, prefix: string | null, suffix: string | null) =>
	`${prefix ?? ""}${value}${suffix ?? ""}`;

const formatPattern = (prefix: string | null, suffix: string | null) =>
	`${prefix ?? ""}###${suffix ?? ""}`;

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => (
	<div className="flex flex-col">
		<span className="text-[11px] text-tertiary mb-[4px]">{label}</span>
		<div className="bg-surface-neutral rounded px-[10px] py-[8px] text-[13px] text-primary font-mono">
			{value}
		</div>
	</div>
);

const NextBlockForm = ({
	counter,
	submitting,
	onSubmit,
}: {
	counter: Dtos.BondNumber.GetAll.Response[number];
	submitting: boolean;
	onSubmit: (block: Dtos.BondNumber.SetNextBlock.Request) => void;
}) => {
	const { prefix, suffix } = counter;

	const { control, handleSubmit } = useForm({
		resolver: typedZodResolver<{ start: number; end: number }, any, { start: number; end: number }>(
			Dtos.BondNumber.SetNextBlock.Request.schema,
		),
	});

	return (
		<form
			onSubmit={handleSubmit((data) => onSubmit({ start: data.start, end: data.end }))}
			className="flex flex-col gap-[10px]"
		>
			<div className="grid grid-cols-2 gap-[10px]">
				<div className="flex flex-col">
					<span className="text-[11px] text-tertiary mb-[4px]">Start</span>
					<FormNumber
						name="start"
						control={control}
						compact
						prefix={prefix}
						suffix={suffix}
						testId="next-block-start-input"
					/>
				</div>
				<div className="flex flex-col">
					<span className="text-[11px] text-tertiary mb-[4px]">End</span>
					<FormNumber
						name="end"
						control={control}
						compact
						prefix={prefix}
						suffix={suffix}
						testId="next-block-end-input"
					/>
				</div>
			</div>
			<Button
				data-testid={`submit-next-${counter.carrierName}`}
				color="primary"
				filled
				className="w-full"
				type="submit"
				disabled={submitting}
			>
				Add Next Range
			</Button>
		</form>
	);
};

export const BondNumberCard = ({
	counter,
	submitting,
	onSubmit,
}: {
	counter: Dtos.BondNumber.GetAll.Response[number];
	submitting: boolean;
	onSubmit: (block: Dtos.BondNumber.SetNextBlock.Request) => void;
}) => {
	const { prefix, suffix, carrierName } = counter;
	const isExhausted = counter.current !== null && counter.current.value === counter.current.end;

	return (
		<div
			data-testid={`bond-number-card-${carrierName}`}
			className="border border-bc-default rounded-lg p-[24px] min-h-fit"
		>
			<div className="flex items-center gap-[12px] mb-[24px]">
				<div className="flex items-center justify-center shrink-0">
					{carrierLogoIconMap[carrierName] ?? (
						<div className="flex items-center justify-center w-[36px] h-[36px] rounded-full bg-surface-neutral text-secondary font-semibold text-[16px]">
							{(prefix ?? carrierName[0] ?? "?").charAt(0).toUpperCase()}
						</div>
					)}
				</div>
				<span className="text-[18px] font-medium text-primary">{counter.carrierDisplayName}</span>
				<span className="text-[13px] text-tertiary">Format:</span>
				<span className="text-[13px] font-mono bg-surface-neutral text-secondary px-[8px] py-[2px] rounded">
					{formatPattern(prefix, suffix)}
				</span>
			</div>

			<div className="grid grid-cols-2 gap-[32px]">
				<div className="flex-1">
					<div className="flex items-center gap-[6px] mb-[12px]">
						<span className="text-[13px] font-semibold text-primary">Current Range</span>
						{isExhausted && (
							<span className="text-[11px] font-medium text-danger bg-surface-danger px-[6px] py-[1px] rounded">
								Exhausted
							</span>
						)}
					</div>
					{counter.current ? (
						<div className="grid grid-cols-2 gap-[10px]">
							<ReadOnlyField
								label="Last Issued"
								value={formatBondNumber(counter.current.value, prefix, suffix)}
							/>
							<ReadOnlyField
								label="End"
								value={formatBondNumber(counter.current.end, prefix, suffix)}
							/>
						</div>
					) : (
						<div className="text-[13px] text-tertiary py-[8px]">No active range</div>
					)}
				</div>

				<div className="flex-1">
					<div className="flex items-center gap-[6px] mb-[12px]">
						<span className="text-[13px] font-semibold text-primary">Next Range</span>
					</div>
					{counter.next ? (
						<div className="grid grid-cols-2 gap-[10px]">
							<ReadOnlyField
								label="Start"
								value={formatBondNumber(counter.next.start, prefix, suffix)}
							/>
							<ReadOnlyField
								label="End"
								value={formatBondNumber(counter.next.end, prefix, suffix)}
							/>
						</div>
					) : (
						<NextBlockForm counter={counter} submitting={submitting} onSubmit={onSubmit} />
					)}
				</div>
			</div>
		</div>
	);
};
